import { webApi } from '@/shared/api/WebApi';
import type {
  PlaylistListResponse,
  CustomPlaylistView,
  TagPlaylistView,
  CustomPlaylistData,
  TagPlaylistData,
  CreateCustomPlaylistRequest,
  CreateTagPlaylistRequest,
  UpdateCustomPlaylistRequest,
  UpdateTagPlaylistRequest,
} from '@/lib/types/Playlist';

export class PlaylistApi {
  /** Fetch all playlists (custom + tag) for a customer */
  static async getPlaylists(cId: number): Promise<PlaylistListResponse> {
    return webApi.get<PlaylistListResponse>('list', { cId });
  }

  /** Fetch single custom playlist with full detail (media positions + pfiles) */
  static async getCustomPlaylist(cId: number, listId: number): Promise<CustomPlaylistView> {
    return webApi.get<CustomPlaylistView>(`list/custom/${listId}`, { cId });
  }

  /** Fetch single tag playlist with full detail */
  static async getTagPlaylist(cId: number, listId: number): Promise<TagPlaylistView> {
    return webApi.get<TagPlaylistView>(`list/tag/${listId}`, { cId });
  }

  /** Create a manual (custom) playlist */
  static async createCustomPlaylist(cId: number, req: CreateCustomPlaylistRequest): Promise<CustomPlaylistData> {
    return webApi.post<CustomPlaylistData>('list/custom', { cId }, req);
  }

  /** Create a smart (tag) playlist */
  static async createTagPlaylist(cId: number, req: CreateTagPlaylistRequest): Promise<TagPlaylistData> {
    return webApi.post<TagPlaylistData>('list/tag', { cId }, req);
  }

  /** Update a manual playlist (settings + media positions for reordering) */
  static async updateCustomPlaylist(
    cId: number, listId: number, req: UpdateCustomPlaylistRequest
  ): Promise<CustomPlaylistView> {
    return webApi.post<CustomPlaylistView>(`list/custom/${listId}`, { cId }, req);
  }

  /** Update a smart playlist (settings + tag rules, sort, limit) */
  static async updateTagPlaylist(
    cId: number, listId: number, req: UpdateTagPlaylistRequest
  ): Promise<TagPlaylistView> {
    return webApi.post<TagPlaylistView>(`list/tag/${listId}`, { cId }, req);
  }

  /** Activate playlists (batch) */
  static async activatePlaylists(cId: number, listIds: number[]): Promise<void> {
    return webApi.post('list/activate', { cId }, { listIds });
  }

  /** Deactivate playlists (batch) */
  static async deactivatePlaylists(cId: number, listIds: number[]): Promise<void> {
    return webApi.post('list/deactivate', { cId }, { listIds });
  }

  /** Delete playlists (batch) */
  static async deletePlaylists(cId: number, listIds: number[]): Promise<void> {
    return webApi.post('list/delete', { cId }, { listIds });
  }
}
